import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import { readObservedEmployerSector, buildSectorMismatchIssueStatement } from './employerObservedSector';
import type { ReviewReason, ReviewReasonEvidence } from './reviewReason.types';

export type RoleMismatchContext = {
  declaredIndustry: string | null;
  employerName: string | null;
  jobTitle: string | null;
  employerObservedSector: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function structuralEvidence(evidence: Record<string, unknown> | null): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const structural = (nested as Record<string, unknown>).STRUCTURAL_SANITY;
    if (structural && typeof structural === 'object' && !Array.isArray(structural)) {
      return { ...evidence, ...(structural as Record<string, unknown>) };
    }
  }
  const gateEv = evidence.STRUCTURAL_SANITY;
  if (gateEv && typeof gateEv === 'object' && !Array.isArray(gateEv)) {
    return { ...evidence, ...(gateEv as Record<string, unknown>) };
  }
  return evidence;
}

/** Pull declared industry / role fields from verification evidence. */
export function readRoleMismatchContext(input: {
  detail?: EmployerVerificationDetail | null;
  footprint?: EmployerFootprintAdminViewDto | null;
  declaredEmployer?: string | null;
  declaredJobTitle?: string | null;
}): RoleMismatchContext {
  const detail = input.detail ?? null;
  const evidence = asRecord(detail?.evidence);
  const struct = structuralEvidence(evidence);
  const snap = asRecord(evidence?.requestSnapshot);

  const declaredIndustry =
    readString(struct.applicationSectorLabel) ??
    readString(struct.resolvedDeclaredIndustry) ??
    readString(snap?.applicationSectorLabel) ??
    readString(snap?.resolvedDeclaredIndustry) ??
    readString(struct.sector) ??
    readString(snap?.sector);

  const jobTitle =
    input.declaredJobTitle?.trim() ||
    readString(struct.role) ||
    readString(snap?.jobTitle) ||
    readString(struct.jobTitle) ||
    null;

  const employerName =
    input.declaredEmployer?.trim() ||
    readString(detail?.employer?.legalName) ||
    null;

  const employerObservedSector = readObservedEmployerSector({
    detail,
    footprint: input.footprint,
  });

  return { declaredIndustry, employerName, jobTitle, employerObservedSector };
}

function buildRoleMismatchEvidence(ctx: RoleMismatchContext): ReviewReasonEvidence[] {
  const rows: ReviewReasonEvidence[] = [];
  if (ctx.declaredIndustry) {
    rows.push({ label: 'Customer declared', value: ctx.declaredIndustry, tone: 'declared' });
  }
  if (ctx.employerObservedSector) {
    rows.push({
      label: 'Employer sector (observed)',
      value: ctx.employerObservedSector,
      tone: 'observed',
    });
  }
  if (ctx.employerName) {
    rows.push({ label: 'Employer', value: ctx.employerName, tone: 'employer' });
  }
  if (ctx.jobTitle) {
    rows.push({ label: 'Role', value: ctx.jobTitle, tone: 'role' });
  }
  return rows.slice(0, 4);
}

/** One-line issue statement for the summary card. */
export function buildRoleMismatchExplanation(ctx: RoleMismatchContext): string {
  return buildSectorMismatchIssueStatement({
    declaredIndustry: ctx.declaredIndustry,
    observedSector: ctx.employerObservedSector,
    employerName: ctx.employerName,
  });
}

export function buildRoleMismatchTitle(ctx: RoleMismatchContext): string {
  if (ctx.declaredIndustry && (ctx.employerObservedSector || ctx.employerName)) {
    return 'Declared industry does not match the employer';
  }
  return 'Job role does not clearly fit the employer';
}

export function enrichRoleMismatchFinding(
  reason: ReviewReason,
  ctx: RoleMismatchContext,
): ReviewReason {
  if (reason.id !== 'role-mismatch') return reason;

  const evidence = buildRoleMismatchEvidence(ctx);
  if (!evidence.length) return reason;

  return {
    ...reason,
    title: buildRoleMismatchTitle(ctx),
    explanation: buildRoleMismatchExplanation(ctx),
    evidence,
  };
}
